import { z } from 'zod';
import type { AttachedEntityKind } from '@leanin/shared';
import { POST_MEDIA_CONTENT_TYPES, type PostMediaContentType } from '@leanin/shared/post-media';

import type { LinkPreviewInput } from '../../lib/mutations/feed';
import { parseStoredPollDraft, type PollDraft } from './poll-draft';

// Circle/network are read-side legacy attachment kinds a draft can still
// carry (pre-dating the create-input narrowing) — `isAttachableInputKind`
// filters them out downstream. Rejecting them here would silently discard
// otherwise-valid older drafts instead of degrading them the same way a
// live re-resolve does.
const DRAFT_ATTACHMENT_KINDS = [
  'resource',
  'event',
  'circle',
  'network',
  'profile',
  'post',
] as const;

const draftMedia = z.object({
  key: z.string().min(1),
  contentType: z.enum(POST_MEDIA_CONTENT_TYPES),
  sizeBytes: z.number().nonnegative(),
  width: z.number().optional(),
  height: z.number().optional(),
  altText: z.string(),
  decorative: z.boolean(),
});

const draftAttachment = z.object({
  kind: z.enum(DRAFT_ATTACHMENT_KINDS),
  id: z.string().min(1),
});

const draftLinkPreview = z.object({
  url: z.string().min(1),
  title: z.string().nullable(),
  description: z.string().nullable(),
  imageStorageKey: z.string().nullable(),
  siteName: z.string().nullable(),
});

// v3 drafts hold bodies in the legacy editor dialect; loading one normalizes
// the body into the current dialect and the next save re-writes it as the
// current version. v5 adds `poll`; v4 drafts (which predate it) are dropped
// on load rather than deserialized with a missing field.
const storedComposerDraft = z.object({
  v: z.union([z.literal(3), z.literal(5)]),
  body: z.string(),
  // Optional so drafts saved before mention chips existed still load.
  mentionNames: z.record(z.string(), z.string()).optional(),
  attachments: z.array(draftAttachment),
  media: z.array(draftMedia),
  linkPreviews: z.array(draftLinkPreview),
  audienceSpaceIds: z.array(z.string()).optional(),
  // The poll's own shape is validated independently by
  // `parseStoredPollDraft` (older builds omit `visibility`), so it is left
  // unvalidated here rather than checked twice.
  poll: z.unknown().nullable().optional(),
});

/** A composer draft restored from localStorage, validated into a shape the
 *  rest of the composer can trust without further null-checks or casts.
 *  `poll` is always the fully-parsed `PollDraft` (never the raw stored
 *  value) — `null` covers both "no poll" and "poll couldn't be recovered". */
export type StoredComposerDraft = {
  v: 3 | 5;
  body: string;
  mentionNames?: Record<string, string>;
  attachments: Array<{ kind: AttachedEntityKind; id: string }>;
  media: Array<{
    key: string;
    contentType: PostMediaContentType;
    sizeBytes: number;
    width?: number;
    height?: number;
    altText: string;
    decorative: boolean;
  }>;
  linkPreviews: LinkPreviewInput[];
  audienceSpaceIds?: string[];
  poll: PollDraft | null;
};

/** Parse a composer draft read back from localStorage (untrusted: written by
 *  an older build, a different draft version, or hand-edited). Returns null
 *  when the value isn't a recognized draft shape — callers drop the whole
 *  draft rather than partially apply it. Does not itself parse JSON; callers
 *  handle a non-JSON `raw` string at the `JSON.parse` call site. */
export function parseStoredComposerDraft(value: unknown): StoredComposerDraft | null {
  const result = storedComposerDraft.safeParse(value);
  if (!result.success) return null;
  return { ...result.data, poll: parseStoredPollDraft(result.data.poll) };
}
